/**!*******************************************************************
 *
 * Copyright (c) Epic AI GmbH. All rights reserved.
 * Author:  Alexander Claas
 * 
 ********************************************************************!*/

import { defineComponent, PropType } from "vue";
import { InputValidator } from "@/classes/InputValidator";

export default defineComponent({
  props: {
    variant: {
      type: String as PropType<"outlined" | "filled" | "flat">,
      default: "outlined",
    },
    modelValue: {
      type: [String, Number],
      default: ""
    },
    type: {
      type: String,
      default: "text"
    },
    required: Boolean,
    disabled: Boolean,
    icon: String,
    trailingIcon: String,
    placeholder: {
      type: String,
      default: ""
    },
    label: String,
    inputValidator: Object as PropType<InputValidator>,
    error: {
      type: [String, Boolean],
      default: ""
    },
    size: String as PropType<"normal" | "small">,
    maxCharacters: Number,
    align: String as PropType<"left" | "center" | "right">
  },
  emits: ["update:modelValue", "update:text", "focus", "blur", "change", "keydown"],
  computed: {
    placeholderValue(): string {
      return this.focused ? this.placeholder : "";
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler (newValue) {
        this.hasValue = newValue.toString().length > 0;
      }
    }
  },
  data() {
    return {
      hasValue: false,
      focused: false,
      errorMessage: ""
    }
  },
  methods: {
    focusIn() {
      (this.$refs["input"] as HTMLInputElement).focus();
    },
    onFocus(event) {
      this.focused = true;
      this.$emit("focus", event);
    },
    onBlur() {
      this.focused = false;
      this.$emit("blur");
      this.validate();
    },
    onChange(event: Event) {
      const value = (event.target as HTMLInputElement).value;
      this.hasValue = value.toString().length > 0;
      this.$emit("update:modelValue", value);
      this.$emit("update:text", value);
    },
    onKeydown(event: KeyboardEvent){
      if(this.maxCharacters && (event.target as HTMLInputElement).value.length >= this.maxCharacters) return event.preventDefault();
      this.$emit("keydown", event);
    },
    validate() {
      this.inputValidator?.updateValue(this.modelValue);
      this.inputValidator?.isValid();
      this.errorMessage = this.inputValidator?.getErrorMessage() || "";
    },
    setErrorMessage(message: string){
      this.errorMessage = message;
    },
  },
});
 